import React, {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import type { DropdownProps } from './types';

// ─── Dropdown ─────────────────────────────────────────────────────────────────

export function Dropdown({
  trigger,
  items,
  align = 'left',
  open: controlledOpen,
  onOpenChange,
}: DropdownProps) {
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const menuId     = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const menuRef    = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  // Portal coordinates — computed from trigger bounding rect so the menu can
  // escape parent stacking contexts (e.g. elements with backdrop-blur) that
  // would otherwise trap z-index.
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; minWidth: number } | null>(null);

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuEl = menuRef.current;
    const menuWidth = menuEl?.offsetWidth ?? 160;
    const top = rect.bottom + 4; // 4px gap (matches mt-1)
    const left = align === 'right' ? rect.right - menuWidth : rect.left;
    setMenuPos({ top, left, minWidth: rect.width });
  }, [align]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    updateMenuPosition();
    // Keep the menu anchored if the user resizes or scrolls.
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [isOpen, updateMenuPosition]);

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  // Auto-focus first enabled menuitem when menu opens
  useEffect(() => {
    if (isOpen && menuRef.current) {
      const first = menuRef.current.querySelector<HTMLElement>(
        '[role="menuitem"]:not([aria-disabled="true"])',
      );
      first?.focus();
    }
  }, [isOpen]);

  // Close on click outside — check both trigger wrapper and portalled menu.
  useEffect(() => {
    if (!isOpen) return;

    function handlePointerDown(e: PointerEvent) {
      const target = e.target as Node;
      const insideTrigger = wrapperRef.current?.contains(target);
      const insideMenu = menuRef.current?.contains(target);
      if (!insideTrigger && !insideMenu) {
        setOpen(false);
      }
    }

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isOpen, setOpen]);

  function getMenuItems(): HTMLElement[] {
    if (!menuRef.current) return [];
    return Array.from(
      menuRef.current.querySelectorAll<HTMLElement>(
        '[role="menuitem"]:not([aria-disabled="true"])',
      ),
    );
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (!isOpen) {
      // Open menu on ArrowDown/ArrowUp when trigger is focused
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }

    const menuItems = getMenuItems();
    const focused   = document.activeElement as HTMLElement;
    const idx       = menuItems.indexOf(focused);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        menuItems[(idx + 1) % menuItems.length]?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        menuItems[(idx - 1 + menuItems.length) % menuItems.length]?.focus();
        break;
      case 'Home':
        e.preventDefault();
        menuItems[0]?.focus();
        break;
      case 'End':
        e.preventDefault();
        menuItems[menuItems.length - 1]?.focus();
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        break;
      case 'Tab':
        // Close on Tab; let focus move naturally
        setOpen(false);
        break;
    }
  }

  function handleTriggerClick() {
    setOpen(!isOpen);
  }

  // Build trigger element with aria props injected
  let triggerEl: React.ReactNode;
  if (isValidElement(trigger)) {
    triggerEl = cloneElement(trigger as React.ReactElement<Record<string, unknown>>, {
      'aria-haspopup': 'menu',
      'aria-expanded': isOpen,
      'aria-controls': isOpen ? menuId : undefined,
      onClick: (e: React.MouseEvent) => {
        const original = (
          trigger as React.ReactElement<{ onClick?: (e: React.MouseEvent) => void }>
        ).props.onClick;
        original?.(e);
        handleTriggerClick();
      },
      ref: (node: HTMLElement | null) => {
        triggerRef.current = node;
      },
    });
  } else {
    triggerEl = (
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        onClick={handleTriggerClick}
        ref={triggerRef as React.Ref<HTMLButtonElement>}
        className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
      >
        {trigger}
      </button>
    );
  }

  // Scale-in state classes
  const menuVisible = isOpen
    ? 'opacity-100 scale-100'
    : 'opacity-0 scale-95 pointer-events-none';

  // Portalled menu panel — rendered to document.body so it escapes any
  // parent stacking context (e.g. backdrop-blur). Position is anchored
  // to the trigger's bounding rect via `fixed` coordinates.
  const menuPanel = (
    <div
      ref={menuRef}
      id={menuId}
      role="menu"
      aria-orientation="vertical"
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      style={{
        position: 'fixed',
        top: menuPos?.top ?? 0,
        left: menuPos?.left ?? 0,
      }}
      className={[
        'z-[9999] min-w-[160px] w-max rounded-md',
        'bg-white dark:bg-neutral-800',
        'border border-neutral-200 dark:border-neutral-700',
        'shadow-lg dark:shadow-neutral-900/60',
        'py-1 outline-none',
        'origin-top transition-all duration-200',
        menuVisible,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {renderItems()}
    </div>
  );

  return (
    <div
      ref={wrapperRef}
      className="relative inline-block"
      onKeyDown={handleKeyDown}
    >
      {/* Trigger */}
      {triggerEl}

      {/* Menu panel (portalled to body) */}
      {typeof document !== 'undefined' && createPortal(menuPanel, document.body)}
    </div>
  );

  function renderItems() {
    return (
      <>
        {items.map((item, i) => {
          const isDisabled = item.disabled === true;
          const isDanger   = item.danger   === true;

          const itemClasses = [
            'flex w-full items-center gap-2 px-3 py-2 text-sm text-left select-none',
            'transition-colors duration-100',
            // Normal item colours
            !isDanger && !isDisabled
              ? 'text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700'
              : '',
            // Danger item colours
            isDanger && !isDisabled
              ? 'text-danger-700 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/30'
              : '',
            // Disabled state
            isDisabled
              ? 'opacity-50 cursor-not-allowed pointer-events-none text-neutral-400 dark:text-neutral-500'
              : 'cursor-pointer',
            'focus-visible:outline-none focus-visible:bg-neutral-100 dark:focus-visible:bg-neutral-700',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <React.Fragment key={i}>
              {item.dividerBefore && (
                <hr className="my-1 border-neutral-200 dark:border-neutral-700" />
              )}
              <button
                type="button"
                role="menuitem"
                aria-disabled={isDisabled || undefined}
                tabIndex={isDisabled ? -1 : 0}
                className={itemClasses}
                onClick={() => {
                  if (isDisabled) return;
                  item.onClick();
                  setOpen(false);
                }}
              >
                {item.icon && (
                  <span className="h-4 w-4 shrink-0 flex items-center" aria-hidden="true">
                    {item.icon}
                  </span>
                )}
                {item.label}
              </button>
            </React.Fragment>
          );
        })}
      </>
    );
  }
}
